import { Dialog, DialogProps } from '@mui/material';
import { useEffect, useRef } from 'react';

import { AnalyticsService } from '@/services';
import { Location } from '@/types';

export const LocalModal = ({
  children,
  disableBackdropClick,
  open,
  onClose,
  name = 'LOCAL MODAL',
  hidePaper = false,
  transparentBackground = false,
  fullScreen = false,
  location,
  disableEvents = false,
  ...muiDialogProps
}: {
  children: React.ReactNode;
  disableBackdropClick?: boolean;
  onClose?: () => void;
  name?: string;
  hidePaper?: boolean;
  transparentBackground?: boolean;
  location?: Location;
  disableEvents?: boolean;
} & Omit<DialogProps, 'onClose'>) => {
  const onMuiDialogClose = (
    _: React.MouseEvent<HTMLButtonElement>,
    reason?: 'backdropClick' | 'escapeKeyDown',
  ) => {
    if (disableBackdropClick && reason === 'backdropClick') return;
    onClose?.();
  };

  // Track previous open state to determine if modal was closed
  const prevOpen = useRef<boolean>(false);
  useEffect(() => {
    setTimeout(() => {
      prevOpen.current = open;
    }, 0);
  }, [open]);

  useEffect(() => {
    if (disableEvents) return;
    if (open) {
      AnalyticsService.showModal(name, location);
    } else if (prevOpen.current) {
      AnalyticsService.closeModal(name, location);
    }
  }, [open, name, prevOpen, disableEvents, location]);

  return (
    <Dialog
      open={open}
      onClose={onMuiDialogClose}
      fullScreen={fullScreen}
      slotProps={{
        paper: {
          sx: [
            {
              backgroundColor: transparentBackground
                ? 'transparent'
                : 'surface.background',
              borderRadius: fullScreen ? 0 : 4,
              boxShadow: '0px 14px 19px 0px rgba(0, 0, 0, 0.25)',
              maxWidth: muiDialogProps.maxWidth || 'none',
              color: 'text.main',
            },
            hidePaper && {
              backgroundColor: 'transparent',
              borderRadius: 0,
              boxShadow: 'none',
              margin: 0,
            },
          ],
        },
        backdrop: {
          sx: {
            backdropFilter: 'blur(20px)',
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
          },
        },
      }}
      {...muiDialogProps}>
      {open && children}
    </Dialog>
  );
};
